import { useEventLogging } from '@AppModels/Logging';
import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { baseStyleSheet } from '../../../styles/styles';
import { Checkbox } from '../../Checkbox';
import { ContactsListItem } from '../../contacts/ContactsListItem';
import { Dropdown } from '../../Dropdown';
import { LoadingSpinner } from '../../LoadingSpinner';
import { DisclosureIcon } from '../../svgs/icons/DisclosureIcon';
import { SuccessCheckIcon } from '../../svgs/icons/SuccessCheckIcon';
import { AutomationSteps } from '../AutomationSteps';
import { SearchEmployeeModal } from './SearchEmployeeModal';
import { styleSheet } from './styles';
import {
	useAutomationOnHoldContactsQuery,
	useApproveAutomationContactsMutation,
	useRejectAutomationContactsMutation,
	invalidateAllAutomationsOnHoldQueries,
	invalidateAllAutomationOnHoldContactsQueries,
} from '../../../../queries/Automation';

enum AutomationSelectionType {
	Myself = 'Myself',
	ContactOwner = 'Contact Owner',
	Employee = 'Employee',
}

interface IProps {
	automationOnHold: Api.IAutomationOnHold;
	className?: string;
	includeAll?: boolean;
	onApproveCompleted?(templateId: string, wasIncludeAll: boolean): void;
	onSelectionChange?(templateId: string, hasSelection: boolean): void;
	styles?: StyleDeclarationValue[];
}

export interface AutomationOnHoldRef {
	approveSelected(): void;
	hasSelectedContacts(): boolean;
	templateId: string | undefined;
}

export const AutomationOnHold = React.forwardRef<AutomationOnHoldRef, IProps>(
	({ automationOnHold, className, includeAll, onApproveCompleted, onSelectionChange, styles }, ref) => {
		const userSession = useUserSession();
		const errorMessages = useErrorMessages();
		const { logInput: _logInput, logApiError } = useEventLogging('AutomationOnHold');

		const [showActionMenu, setShowActionMenu] = React.useState(false);
		const [showSearchModal, setShowSearchModal] = React.useState(false);
		const [sendAutomationAsType, setSendAutomationAsType] = React.useState<AutomationSelectionType>(
			AutomationSelectionType.Myself
		);
		const [sendAutomationAsUser, setSendAutomationAsUser] = React.useState<Api.IUser | null>(null);
		const [selectAllChecked, setSelectAllChecked] = React.useState(false);
		const [selectedContactIds, setSelectedContactIds] = React.useState<Set<string>>(new Set());
		const [excludedContactIds, setExcludedContactIds] = React.useState<Set<string>>(new Set());
		const [approvedContactIds, setApprovedContactIds] = React.useState<Set<string>>(new Set());
		const [isBusy, setIsBusy] = React.useState(false);

		const templateId = automationOnHold?.template?.id;

		const isAdmin = React.useMemo(
			() => userSession.account.isAdmin && userSession.account.features.automation.allowAdminToStartOnBehalf,
			[userSession]
		);

		const { data: contactsData, isFetching: _isFetching } = useAutomationOnHoldContactsQuery({
			templateId,
			includeAll,
			enabled: !!templateId,
		});

		// Ensure contacts is an array to prevent TypeError when data is undefined.
		const contacts = React.useMemo(() => (Array.isArray(contactsData) ? contactsData : []), [contactsData]);

		const approveMutation = useApproveAutomationContactsMutation({
			onSuccess: data => {
				if (data && data.length > 0) {
					const approvedIds = new Set(selectedContactIds);
					setApprovedContactIds(prev => new Set([...prev, ...approvedIds]));
					setSelectedContactIds(new Set());
					setExcludedContactIds(new Set());
					setSelectAllChecked(false);

					if (approvedIds.size === contacts.length) {
						onApproveCompleted?.(templateId, includeAll || false);
					}

					invalidateAllAutomationsOnHoldQueries();
					invalidateAllAutomationOnHoldContactsQueries();
				}
				setIsBusy(false);
			},
			onError: error => {
				errorMessages.pushApiError(error);
				logApiError('ApproveContacts-Error', error);
				setIsBusy(false);
			},
		});

		const rejectMutation = useRejectAutomationContactsMutation({
			onSuccess: data => {
				if (data) {
					const rejectedIds = selectAllChecked
						? new Set(contacts.map(c => c.id).filter((id): id is string => id !== undefined))
						: selectedContactIds;

					setSelectedContactIds(prev => {
						const newSet = new Set(prev);
						rejectedIds.forEach(id => newSet.delete(id));
						return newSet;
					});

					setExcludedContactIds(new Set());
					setSelectAllChecked(false);

					invalidateAllAutomationsOnHoldQueries();
					invalidateAllAutomationOnHoldContactsQueries();
				}
				setIsBusy(false);
			},
			onError: error => {
				errorMessages.pushApiError(error);
				logApiError('RejectContacts-Error', error);
				setIsBusy(false);
			},
		});

		const allContactsApproved = React.useMemo(() => {
			return contacts.length > 0 && approvedContactIds.size === contacts.length;
		}, [contacts.length, approvedContactIds.size]);

		const _selectedContacts = React.useMemo(() => {
			return contacts.filter(c => c.id && selectedContactIds.has(c.id));
		}, [contacts, selectedContactIds]);

		const getFullName = React.useCallback((user: Api.IUser) => {
			return user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.email;
		}, []);

		const toggleActionMenu = React.useCallback(() => {
			setShowActionMenu(prev => !prev);
		}, []);

		const sendAutomationAs = React.useCallback((type: AutomationSelectionType) => {
			return () => {
				setSendAutomationAsType(type);
				setSendAutomationAsUser(null);
				setShowActionMenu(false);
				setShowSearchModal(type === AutomationSelectionType.Employee);
			};
		}, []);

		const onSelectionMade = React.useCallback((selection: Api.IUser) => {
			setSendAutomationAsUser(selection);
			setShowSearchModal(false);
		}, []);

		const onModalRequestClose = React.useCallback(() => {
			setSendAutomationAsType(AutomationSelectionType.Myself);
			setSendAutomationAsUser(null);
			setShowSearchModal(false);
		}, []);

		const toggleSelectAll = React.useCallback(() => {
			setSelectAllChecked(prev => !prev);
			if (!selectAllChecked) {
				setSelectedContactIds(new Set());
				setExcludedContactIds(new Set());
			}
		}, [selectAllChecked]);

		const toggleContact = React.useCallback(
			(contact: Api.IContact) => {
				return () => {
					const contactId = contact.id;
					if (!contactId) {
						return;
					}

					if (selectAllChecked) {
						setExcludedContactIds(prev => {
							const newSet = new Set(prev);
							if (newSet.has(contactId)) {
								newSet.delete(contactId);
							} else {
								newSet.add(contactId);
							}
							return newSet;
						});
					} else {
						setSelectedContactIds(prev => {
							const newSet = new Set(prev);
							if (newSet.has(contactId)) {
								newSet.delete(contactId);
							} else {
								newSet.add(contactId);
							}
							return newSet;
						});
					}
				};
			},
			[selectAllChecked]
		);

		const approveSelected = React.useCallback(() => {
			if (!templateId) {
				return;
			}

			setIsBusy(true);

			const contactIdsToApprove: string[] = selectAllChecked
				? contacts
						.filter(c => c.id && !excludedContactIds.has(c.id))
						.map(c => c.id)
						.filter((id): id is string => id !== undefined)
				: Array.from(selectedContactIds);

			const createAutomationRequest: Api.ICreateAutomationRequest = {
				includeContactIds: contactIdsToApprove,
			};

			if (sendAutomationAsType === AutomationSelectionType.Employee && sendAutomationAsUser?.id) {
				createAutomationRequest.sendFromOptions = {
					mode: Api.SendEmailFrom.SelectedUser,
					selectedUser: sendAutomationAsUser.id,
				};
			}

			approveMutation.mutate({
				templateId,
				includeAll: includeAll || false,
				createAutomationRequest,
			});
		}, [
			templateId,
			selectAllChecked,
			contacts,
			excludedContactIds,
			selectedContactIds,
			sendAutomationAsType,
			sendAutomationAsUser,
			includeAll,
			approveMutation,
		]);

		const rejectSelected = React.useCallback(() => {
			if (!templateId) {
				return;
			}

			setIsBusy(true);

			const contactIdsToReject: string[] = selectAllChecked
				? contacts
						.filter(c => c.id && !excludedContactIds.has(c.id))
						.map(c => c.id)
						.filter((id): id is string => id !== undefined)
				: Array.from(selectedContactIds);

			const createAutomationRequest: Api.ICreateAutomationRequest = {
				includeContactIds: contactIdsToReject,
			};

			rejectMutation.mutate({
				templateId,
				includeAll: includeAll || false,
				createAutomationRequest,
			});
		}, [templateId, selectAllChecked, contacts, excludedContactIds, selectedContactIds, includeAll, rejectMutation]);

		const hasSelectedContacts = React.useMemo(() => {
			if (selectAllChecked) {
				return contacts.length > excludedContactIds.size;
			}
			return selectedContactIds.size > 0;
		}, [selectAllChecked, contacts.length, excludedContactIds.size, selectedContactIds.size]);

		const isContactSelected = React.useCallback(
			(contact: Api.IContact) => {
				const contactId = contact.id;
				if (!contactId) {
					return false;
				}

				if (selectAllChecked) {
					return !excludedContactIds.has(contactId) && !approvedContactIds.has(contactId);
				}
				return selectedContactIds.has(contactId);
			},
			[selectAllChecked, excludedContactIds, approvedContactIds, selectedContactIds]
		);

		// Expose methods to parent via ref for "Approve all selected" functionality.
		React.useImperativeHandle(ref, () => ({
			approveSelected,
			hasSelectedContacts: () => hasSelectedContacts,
			templateId,
		}));

		// Notify parent of selection changes.
		React.useEffect(() => {
			if (templateId) {
				onSelectionChange?.(templateId, hasSelectedContacts);
			}
		}, [templateId, hasSelectedContacts, onSelectionChange]);

		return (
			<div className={`${css(styleSheet.container, ...(styles || []))} automation-on-hold ${className || ''}`}>
				<article className={css(styleSheet.automationContainer)}>
					<h3 className={css(styleSheet.automationName, baseStyleSheet.truncateText)}>
						{automationOnHold?.template?.name}
					</h3>
					<p className={css(baseStyleSheet.tableColumnHeader, styleSheet.stepsLabel)}>Steps</p>
					<AutomationSteps
						publishedStepReferences={automationOnHold?.template?.publishedSteps}
						draftStepReferences={automationOnHold?.template?.draftSteps}
						status={automationOnHold?.template?.status}
						maxStepsToDisplay={6}
					/>
					{isAdmin ? (
						<div className={css(styleSheet.sendFromFieldWrap)}>
							<p className={css(styleSheet.sendFromLabel)}>Send from: </p>
							<Dropdown
								anchor={
									<button type='button' className={css(styleSheet.dropdownMenuButton)} onClick={toggleActionMenu}>
										<span className={css(styleSheet.nameWrap, baseStyleSheet.truncateText)}>
											{sendAutomationAsType === AutomationSelectionType.Employee && sendAutomationAsUser
												? getFullName(sendAutomationAsUser)
												: sendAutomationAsType}
										</span>
										<DisclosureIcon className={css(styleSheet.triggerCaret)} fillColor='#858585' />
									</button>
								}
								isOpen={showActionMenu}
								contentClassName={css(styleSheet.menu)}
								contentPositionX='left'
								contentPositionY='bottom'
								openOnClick={true}
							>
								<div className={css(styleSheet.menuInnerContainer)}>
									{Object.values(AutomationSelectionType).map(x => {
										return (
											<button
												key={x}
												type='button'
												className={css(styleSheet.resetButton)}
												onClick={sendAutomationAs(x)}
											>
												{x !== AutomationSelectionType.Employee ? x : 'Search Employees'}
											</button>
										);
									})}
								</div>
							</Dropdown>
						</div>
					) : null}
					{isAdmin ? (
						<SearchEmployeeModal
							modalProps={{
								isOpen: showSearchModal,
								onRequestClose: onModalRequestClose,
							}}
							onSelectionMade={onSelectionMade}
						/>
					) : null}
				</article>
				<div className={css(styleSheet.contactListContainer)}>
					<div className={css(styleSheet.actionContainer)}>
						<Checkbox
							checked={selectAllChecked}
							className={css(styleSheet.checkbox)}
							disabled={isBusy || allContactsApproved}
							id={`contacts-list-select-all-checkbox-${automationOnHold.template.id}`}
							onChange={toggleSelectAll}
							partial={selectAllChecked && excludedContactIds.size > 0}
						>
							<span>Select all {contacts.length}</span>
						</Checkbox>
						<button
							className={css(baseStyleSheet.ctaButtonSmall)}
							disabled={isBusy || !hasSelectedContacts || allContactsApproved}
							onClick={approveSelected}
						>
							<span>Approve selected</span>
						</button>
						<button
							className={css(baseStyleSheet.ctaButtonDestructiveSmall)}
							disabled={isBusy || !hasSelectedContacts || allContactsApproved}
							onClick={rejectSelected}
						>
							<span>Reject selected</span>
						</button>
					</div>
					<div className={css(styleSheet.contactList, allContactsApproved && styleSheet.disableScroll)}>
						{allContactsApproved && !isBusy && (
							<div className={css(styleSheet.successCover)}>
								<SuccessCheckIcon size={60} />
							</div>
						)}
						{contacts.map(contact => {
							const unique = `${automationOnHold.template.id}-${contact.id}`;
							const showSelected = isContactSelected(contact);
							const isApproved = contact.id ? approvedContactIds.has(contact.id) : false;

							return (
								<ContactsListItem
									className={css(styleSheet.contactListItem)}
									contact={contact}
									key={unique}
									selected={showSelected}
									checkboxId={unique}
									success={isApproved}
									onCheckChanged={toggleContact(contact)}
								/>
							);
						})}
						{isBusy && (
							<div className={css(styleSheet.contactListItem)}>
								<LoadingSpinner type='small' />
							</div>
						)}
					</div>
				</div>
			</div>
		);
	}
);

AutomationOnHold.displayName = 'AutomationOnHold';
